<template>
<div :id="`server--${server.name.toLowerCase()}`" 
    class="server"
    :class="{'server--selected': watchedServers.includes(server.name)}">

  <!-- Basic Server Info (Name, Status) -->
  <div @click.prevent="toggleWatchedServer(server.name)"
      class="server-info">

    <v-tooltip v-if="statusBadge && server.status !== null"
              target="serverStatus"
              :content="server.status === 'Online' ? 'Server is online' : `Last online ${lastOnline(server)} ago`"
              :delay="500">
    </v-tooltip>
    <span v-if="statusBadge && server.status !== null"
          ref="serverStatus"
          :class="`server-info__status server-info__status--${server.status.toLowerCase()}`">
      <span class="text">{{ server.status }}</span>
    </span>

    <span class="server-info__name">
      <span class="text">{{ server.name }}</span>
    </span>

    <v-tooltip v-if="congestionBadge && server.congestion !== null"
              target="serverCongestion"
              :content="parseCongestionMessage(server.congestion)"
              :delay="500">
    </v-tooltip>
    <span v-if="congestionBadge && server.congestion !== null"
          ref="serverCongestion"
          :class="`server-info__congestion server-info__congestion--abbr server-info__congestion--${server.congestion.toLowerCase()}`">
      <span class="text">{{ parseCongestionLevelAbbr(server.congestion) }}</span>
    </span>

    <v-tooltip v-if="creationBadge && server.creation !== null" 
              target="serverCreation"
              :content="`Character creation is ${server.creation.toLowerCase()}`"
              :delay="500">
    </v-tooltip>
    <div v-if="creationBadge && server.creation !== null"
        ref="serverCreation"
        :class="`server-info__creation-icon server-info__creation-icon--${server.creation.toLowerCase()}`">
      <span class=creation-icon__head></span>
      <span class=creation-icon__body></span>
    </div>
  </div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex' 
import moment from 'moment'
import VTooltip from './../components/Tooltip'

export default {
  components: {
    VTooltip
  },

  props: {
    'server': {
      type: Object,
      required: true
    },
    'status-badge': {
      type: Boolean,
      default: true
    },
    'congestion-badge': {
      type: Boolean,
      default: true
    },
    'creation-badge': {
      type: Boolean,
      default: true
    }
  },

  computed: {
    ...mapGetters({
      'watchedServers': 'getWatchedServers'
    })
  },

  methods: {
    ...mapActions([
      'toggleWatchedServer'
    ]),
    lastOnline(server) {
      let start = moment.utc(server.last_online).toDate(); // This will convert to local time zone from UTC.
      let duration = moment.duration(new moment().diff(start));

      return duration.humanize();
    },
    parseCongestionLevelAbbr(congestion) {
      if (congestion === 'Standard') {
        return 'STD';
      } else if (congestion === 'Preferred') {
        return 'PRF';
      } else if (congestion === 'New') {
        return 'NEW';
      } else if (congestion === 'Congested') {
        return 'CNG';
      } else {
        return '???';
      }
    },
    parseCongestionMessage(congestion) {
      if (congestion === 'Standard') {
        return `The server's congestion level is average`;
      } else if (congestion === 'Preferred') {
        return `The server's congestion level is low and is a preferred server`;
      } else if (congestion === 'New') {
        return `The server is new and the congestion level is low`;
      } else if (congestion === 'Congested') {
        return `The server is congested`;
      } else {
        return '???';
      }
    }
  }
}
</script>

<style lang="scss" type="text/scss" scoped>
.server {
  color: $color-white;
  background-color: $color-dark-2;
  transition: 
    background-color 1s ease, 
    top 0.5s ease, 
    height 0.5s ease;
  overflow: hidden;
  height: em(48px); // 48px? (O: 64px) 48-64-80-96

  &:hover {
    background-color: $color-white;

    .server-info__name,
    .server-info__label {
      color: $color-dark-1;
    }
  }
}

.server--selected {
  background-color: $color-white;

  .server-info__name {
    color: $color-dark-1;
  }
}

.server-info {
  display: flex;
  align-items: center;
  height: 100%;
  padding: em(10px);

  &:hover {
    cursor: pointer;
  }
}

.server-info__name {
  position: relative;
  color: $color-white;
  transition: color 1s ease;
  margin: 0 em(10px);
  flex-grow: 1; // Stretch to the left and push the rest to the right.

  .text {
    font-size: em(14px);
  }
}

.server-info__status {
  flex-grow: 0; // We don't want the size of this to change.
  padding: em(3px) em(8px); // 5 and 10 O.
  width: em(60px);
  text-align: center;
  border-radius: em(2px);
  transition: background-color 2s ease;
  box-shadow: $box-shadow--standard;

  .text {
    font-size: em(12px);
  }
}

.server-info__status--online {
  color: rgba(0, 0, 0, 0.75);
  background-color: $color-online;
}

.server-info__status--offline {
  color: rgba(255, 255, 255, 0.85);
  background-color: $color-offline;
}

.server-info__status--maintenance {
  color: rgba(0, 0, 0, 0.75);
  background-color: $color-maintenance;
  width: auto; // The 'maintenance' string is too long for the original width.
}

.server-info__congestion {
  flex-grow: 0; // We don't want the size of this to change.
  padding: em(3px) em(8px); // 5 and 10 O.
  width: em(80px);
  text-align: center;
  border-radius: em(2px);
  transition: background-color 2s ease;
  box-shadow: $box-shadow--standard;
  
  .text {
    font-size: em(12px);
  }
}

.server-info__congestion--abbr {
  width: em(40px);
}

.server-info__congestion--congested {
  color: rgba(255, 255, 255, 0.85);
  background-color: $color-offline;
}

.server-info__congestion--standard {
  color: rgba(0, 0, 0, 0.75);
  background-color: $color-congestion-standard;
}

.server-info__congestion--preferred,
.server-info__congestion--new {
  color: rgba(0, 0, 0, 0.75);
  background-color: $color-congestion-preferred;
}

.server-info__creation {
  flex-grow: 0; // We don't want the size of this to change.
  width: em(80px);
  text-align: center;
  padding: em(3px) em(8px); // 5 and 10 O.
  border-radius: em(2px);
  transition: background-color 2s ease;
  box-shadow: $box-shadow--standard;

  .text {
    font-size: em(12px);
  }
}

.server-info__creation--unavailable {
  color: rgba(255, 255, 255, 0.85);
  background-color: $color-creation-unavailable;
}

.server-info__creation--available {
  color: rgba(0, 0, 0, 0.75);
  background-color: $color-creation-available;
}

.server-info__creation-icon {
  position: relative;
  height: em(25px);
  width: em(25px);
  overflow: hidden;
  margin-left: em(10px);
  margin-bottom: em(3px);
}

.creation-icon__head {
  position: absolute;
  top: 40%;
  left: 50%;
  width: 50%;
  height: 50%;
  transform: translateX(-50%) translateY(-50%);
  background-color: gray;
  border-radius: 50%;
  transition: background-color 2s ease;
}
.creation-icon__body {
  position: absolute;
  top: 100%;
  left: 50%;
  width: 75%;
  height: 75%;
  transform: translateX(-50%) translateY(-50%);
  background-color: gray;
  border-radius: 50%;
  transition: background-color 2s ease;
}

.server-info__creation-icon--available {
  .creation-icon__head,
  .creation-icon__body {
    background-color: $color-creation-available;
  }
}

.server-info__creation-icon--unavailable {
  .creation-icon__head,
  .creation-icon__body {
    background-color: $color-creation-unavailable;
  }
}
</style>